import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ReplaySubject, Subject } from 'rxjs';
import { EnvironmentName, RenderType } from 'situview-webapi-client';
import { PanoramaService } from 'src/app/services';
import { Guid } from 'guid-typescript';

@Component({
  selector: 'app-panorama',
  templateUrl: './panorama.component.html',
  styleUrls: ['./panorama.component.less'],
  providers: []
})
export class PanoramaComponent implements OnInit, AfterViewInit, OnDestroy {

  pluginManager: any;

  @Input() onPluginManagerLoad: ReplaySubject<any> | undefined;
  @Input() onComponentSetup: Subject<any> | undefined;
  @Input() onLoadHome: Subject<any> | undefined;

  @Input() builder: string = '';
  @Input() home: string = '';
  @Input() masterFile: string = '';
  @Input() themes: any;
  @Input() options: any;
  @Input() startingRoom: string = '';
  @Input() viewAngle: number | undefined;
  @Input() env: number | undefined;
  @Input() render: RenderType = RenderType.BestAvailable;

  @Input() overlayElementID: string = '';

  @Output() viewingAngle = new EventEmitter<number>();
  @Output() error = new EventEmitter<string>();

  situSystems: any;

  queryParams: PanoHomeParams = new PanoHomeParams();
  currentParams: PanoHomeParams = new PanoHomeParams();
  panoramaLoadAttempts: number = 0;

  panoramaError: string | null = null;

  plugin: any;
  pluginName: string = '';
  minFov = 85;
  maxFov = 120;
  minWidth = 960;
  maxWidth = 2195;
  ratio = (this.maxWidth - this.minWidth) / (this.maxFov - this.minFov);

  constructor(
    private panoramaService: PanoramaService) {
      this.pluginName = `SituPanoViewer${Math.floor(Date.now() * Math.random())}`;
  }

  ngOnInit() {
    this.onLoadHome?.subscribe((homeConfig) => {
      if (this.pluginManager) {
        this.loadHome(homeConfig);
      }
    });

    window.addEventListener('resize', () => {
      let panoramaDiv = document?.getElementById(this.pluginName);
      let fieldOfView = this.calculateFieldOfView(panoramaDiv?.clientWidth, panoramaDiv?.clientHeight);
      if (fieldOfView && this.pluginManager?.setFieldOfView) {
        this.pluginManager?.setFieldOfView(fieldOfView);
      }
    });
  }

  ngAfterViewInit() {
    this.onPluginManagerLoad?.subscribe((x) => {
      this.init(x);
    });
  }

  ngOnDestroy() {
    if (this.plugin && this.pluginManager) {
      this.pluginManager.removePlugin(this.pluginName);
    }
  }

  calculateFieldOfView(width: number | undefined, height: number | undefined) {
    if (!width || !height) {
      return;
    }

    let currentDiff = width - 960;
    currentDiff = currentDiff < 0 ? 0 : currentDiff;

    let fieldOfView = this.minFov + currentDiff / this.ratio;

    if (fieldOfView < this.minFov)
      fieldOfView = this.minFov;
    if (fieldOfView > this.maxFov)
      fieldOfView = this.maxFov;

    return fieldOfView;
  }

  buildInputParams() {
    this.queryParams = new PanoHomeParams();
    this.queryParams.homeConfig = { builder: this.builder, home: this.home, masterFile: this.masterFile, themes: this.themes, options: this.options };
    this.queryParams.artifactEnvironment = { company: this.builder, environment: this.env };
    this.queryParams.renderType = this.render;
    this.queryParams.startingRoom = this.startingRoom;
    this.queryParams.viewAngle = this.viewAngle;
    this.queryParams.environment = this.env;
  }

  init(pluginManager: any) {
    this.situSystems = this.panoramaService.situSystems;
    this.pluginManager = pluginManager;
    this.buildInputParams();

    let settings = new this.situSystems.PanoramaSettings();
    if (this.overlayElementID) { settings.overlayElementId = this.overlayElementID; }
    let panoramaDiv = document?.getElementById(this.pluginName);
    settings.fieldOfView = this.calculateFieldOfView(panoramaDiv?.clientWidth, panoramaDiv?.clientHeight) ?? settings.fieldOfView;
    settings.minFieldOfView = this.minFov;
    settings.maxFieldOfView = this.maxFov;
    settings.fieldOfViewType = "MFOV";

    this.plugin = new this.situSystems.PanoramaViewer(this.pluginName, settings);
    this.pluginManager.addPlugin(this.plugin);

    this.registerEventListeners();

    let builder = this.queryParams.homeConfig?.builder != undefined ? this.queryParams.homeConfig.builder : null;
    let home = this.queryParams.homeConfig?.home != undefined ? this.queryParams.homeConfig.home : null;
    let masterFile = this.queryParams.homeConfig?.masterFile != undefined ? this.queryParams.homeConfig.masterFile : null;
    let themes = this.queryParams.homeConfig?.themes != undefined ? this.queryParams.homeConfig.themes : [];
    let room = this.queryParams.startingRoom != undefined ? this.queryParams.startingRoom : "kitchen";
    let options = this.queryParams.homeConfig?.options != null ? this.queryParams.homeConfig.options : [];
    let viewingAngle = (this.queryParams.viewAngle != null && !isNaN(this.queryParams.viewAngle)) ? this.queryParams.viewAngle : null;
    let environment = this.queryParams.artifactEnvironment?.environment != null ? this.queryParams.artifactEnvironment.environment : EnvironmentName.Production;
    let renderType = this.queryParams.renderType != null && this.queryParams.renderType != undefined ? this.queryParams.renderType : RenderType.BestAvailable;

    let currentParams = new PanoHomeParams();
    currentParams.homeConfig = { builder: builder, home: home, masterFile: masterFile, themes: themes, options: options };
    currentParams.startingRoom = room;
    currentParams.artifactEnvironment = { company: builder, environment: environment };
    currentParams.environment = environment;
    currentParams.renderType = renderType;

    let roomSettings = [];
    if (viewingAngle != null) {
      roomSettings = [{ name: room, viewingAngle: this.queryParams.viewAngle }];
      currentParams.viewAngle = viewingAngle;
      currentParams.roomSettings = roomSettings;
    }

    this.currentParams = currentParams;

    // We don't want to attempt to load a home if there isn't any params to load.
    if (this.currentParams.homeConfig?.home && this.currentParams.homeConfig.masterFile && this.currentParams?.homeConfig?.themes?.length) {
      this.loadHome(currentParams);
    }
  }

  registerEventListeners() {
    // Updating URL after selecting a room
    this.pluginManager.addEventListener("roomselected", (e: CustomEvent) => {
      if (this.currentParams) {
        this.currentParams.startingRoom = e.detail.roomName;
      }
      this.panoramaError = null;
    });
    this.pluginManager.addEventListener("viewinganglechange", (e: CustomEvent) => {
      if (e.detail.viewingAngle && this.viewingAngle) {
        this.viewingAngle.emit(e.detail.viewingAngle);
      }
    });
    this.pluginManager.addEventListener("homereset", () => {
      this.panoramaError = null;
    });

    this.pluginManager.onError = (error: any) => {
      this.panoramaError = error;
      this.error.emit(this.panoramaError ?? undefined);
    }
  }

  loadHome(homeInformation: any) {
    this.panoramaError = null;
    this.currentParams = homeInformation;

    this.pluginManager.load({
      homeConfig: {
        builder: homeInformation.homeConfig.builder,
        home: homeInformation.homeConfig.home,
        masterFile: homeInformation.homeConfig.masterFile,
        themes: homeInformation.homeConfig.themes,
        options: homeInformation.homeConfig.options,
      },
      startingRoom: homeInformation.startingRoom,
      roomSettings: homeInformation.roomSettings,
      forceRefresh: homeInformation.forceRefresh,
      renderType: homeInformation.renderType,
      environment: homeInformation.environment,
      queueName: homeInformation.renderer
    });
  }
}

class PanoHomeParams {
  artifactEnvironment: {
    environment: number | undefined,
    company: string | null
  } | undefined;
  homeConfig: {
    builder: string | null;
    home: string | null;
    masterFile: string | null;
    themes: any;
    options: any;
  } | undefined;
  renderType: RenderType = RenderType.BestAvailable;
  roomSettings: any;
  startingRoom: string = '';
  viewAngle: number | undefined;
  forceRefresh: boolean = false;
  environment: number | undefined;
}
